"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/hooks/use-api";
import { queryKeys } from "@/lib/query-keys";
import type {
  Broadcast,
  BroadcastAudience,
  BroadcastAudienceCounts,
  BroadcastChannel,
  BroadcastType,
  SendBroadcastDto,
} from "@rr/api";

/**
 * Admin broadcasts — news / discount / blog announcements sent to tenant
 * users. v1 delivers over in-app notifications only (the email channel is
 * disabled in the composer); the backend resolves the audience, fans out
 * notifications, and persists a history row.
 */

export type {
  Broadcast,
  BroadcastAudience,
  BroadcastChannel,
  BroadcastType,
} from "@rr/api";

export type ComposeBroadcastInput = SendBroadcastDto;

/** Sent-broadcast history (most recent first). */
export const useBroadcastHistory = () => {
  const api = useApi();
  return useQuery<Broadcast[]>({
    queryKey: queryKeys.platform.broadcasts(),
    queryFn: async () => {
      const res = await api.platform.listBroadcasts();
      return res?.data ?? [];
    },
  });
};

/** Live recipient counts for the audience picker. */
export const useBroadcastAudienceCounts = () => {
  const api = useApi();
  return useQuery<BroadcastAudienceCounts>({
    queryKey: queryKeys.platform.broadcastAudienceCounts(),
    queryFn: async () => {
      const res = await api.platform.getBroadcastAudienceCounts();
      return res?.data ?? { all: 0, active: 0 };
    },
    // Counts shift slowly (new tenants/users); a short stale window keeps
    // the composer responsive without hammering the endpoint.
    staleTime: 60 * 1000,
  });
};

export const useSendBroadcast = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ComposeBroadcastInput) =>
      api.platform.sendBroadcast(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.broadcasts() });
    },
  });
};

export const useDeleteBroadcast = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.platform.deleteBroadcast(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.broadcasts() });
    },
  });
};
